import { useState, type ReactNode } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Modal, StatusBadge } from "@fawkes/ui";
import { AlertTriangle, ArrowLeft, Check, ClipboardList, History, MessageSquareReply, Repeat, Send, UserCheck, X } from "lucide-react";

import { formatDate, formatDateTime } from "../i18n/format";
import {
  foutBody,
  leesJson,
  toon,
  waardeTekst,
  type Actie,
  type Detail,
  type Driewegvergelijking,
  type Inhoud,
  type Match,
  type ReserveringAanvraag,
} from "./klantverzoeken/types";
import { ReserveringBlok } from "./klantverzoeken/ReserveringBlok";
import { BestandenBlok } from "./klantverzoeken/BestandenBlok";
import { DierNieuwBlok } from "./klantverzoeken/DierNieuwBlok";
import { BoekjeBlok } from "./klantverzoeken/BoekjeBlok";
import "./Klantverzoeken.css";

/**
 * Eén klantverzoek beoordelen (ontwerp Klantportaal Pension, hoofdstuk 10 en 21; mockups "Beoordeling verzoek" en
 * "Beoordeling registratie"). De medewerker past de inhoud van de klant nooit aan: hij vraagt een aanvulling,
 * wijst af, keurt goed zoals gevraagd, of doet een tegenvoorstel dat de klant moet accepteren. Welke knoppen er
 * zijn, zegt de server per status ({@link Detail#acties}).
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

type Venster = "aanvulling" | "afwijzen" | "goedkeuren" | "voorstel" | null;

export function KlantverzoekDetail({
  id,
  onBack,
  onOpenKlant,
}: {
  id: string;
  onBack: () => void;
  onOpenKlant: (klantId: number) => void;
}) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useCustom<Detail>({ url: `klantverzoeken/${id}`, method: "get" });
  const d = data?.data;
  const registratie = d?.type === "KLANT_REGISTRATIE";
  const matches = useCustom<Match[]>({
    url: `klantverzoeken/${id}/matches`,
    method: "get",
    queryOptions: { enabled: registratie },
  }).data?.data;
  const reserveringType = d?.type === "RESERVERING_NIEUW" || d?.type === "RESERVERING_WACHTLIJST";
  const reservering = useCustom<ReserveringAanvraag>({
    url: `klantverzoeken/${id}/reservering`,
    method: "get",
    queryOptions: { enabled: reserveringType },
  });
  const [voorstelPrijs, setVoorstelPrijs] = useState<number | null>(null);
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [venster, setVenster] = useState<Venster>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Driewegvergelijking | null>(null);
  const [koppelKlant, setKoppelKlant] = useState<number | null>(null);

  async function doe(pad: string, waarden: object = {}): Promise<boolean> {
    setFout(null);
    try {
      await mutateAsync({
        url: `klantverzoeken/${id}/${pad}`,
        method: "post",
        values: waarden,
        successNotification: false,
        errorNotification: false,
      });
      setVenster(null);
      setConflict(null);
      await refetch();
      return true;
    } catch (err) {
      const body = foutBody(err);
      if (body.code === "PRIJS_GEWIJZIGD") {
        // Duurder dan de klant zag: eerst een tegenvoorstel met de nieuwe prijs (ontwerp 12).
        setVoorstelPrijs((body.details as { nu?: number } | undefined)?.nu ?? null);
        setFout(body.melding ?? t("klantverzoeken.fout.actie"));
        setVenster("voorstel");
        void reservering.refetch();
        return false;
      }
      if (body.code === "CONFLICT" && body.details) {
        setConflict(body.details as Driewegvergelijking);
      }
      setFout(body.melding ?? t("klantverzoeken.fout.actie"));
      return false;
    }
  }

  if (isLoading) {
    return <p className="kv__laden">{t("common.loading")}</p>;
  }
  if (isError || !d) {
    return <p className="fawkes-error" role="alert">{t("klantverzoeken.fout.laden")}</p>;
  }
  const kan = (a: Actie) => d.acties.includes(a);
  const zichtbareConflict = conflict ?? d.conflict;

  return (
    <div className="kv kvd">
      <button type="button" className="kvd__terug" onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" /> {t("klantverzoeken.titel")}
      </button>

      <header className="kvd__kop">
        <div>
          <h1>
            {t(`klantverzoeken.type.${d.type}`)} <span className="kv__nummer">#{d.nummer}</span>
          </h1>
          <p className="kvd__sub">
            {d.klantId != null ? (
              <button type="button" className="kvd__klantlink" onClick={() => onOpenKlant(d.klantId!)}>
                {d.klantnaam ?? d.huishouden}
              </button>
            ) : (
              <span>
                {d.huishouden} · <strong>{t("klantverzoeken.nieuweKlant")}</strong>
              </span>
            )}
            {" · "}
            {t("klantverzoeken.ingediendDoor", { args: [d.indiener ?? "—", t(`klantverzoeken.kanaal.${d.kanaal}`), formatDateTime(d.ingediend, locale)] })}
          </p>
          {d.geclaimdDoor && <p className="kvd__claim">{t("klantverzoeken.inBehandelingBij", { args: [d.geclaimdDoor] })}</p>}
        </div>
        <StatusBadge tone={toon(d.status)}>{t(`klantverzoeken.status.${d.status}`)}</StatusBadge>
      </header>

      {d.status === "MISLUKT" && (
        <div className="kvd__melding kvd__melding--fout" role="status">
          <AlertTriangle size={18} aria-hidden="true" />
          <div>
            <strong>{t(`klantverzoeken.mislukt.${d.misluktReden ?? "TECHNISCH"}`)}</strong>
            {d.notitieIntern && <p>{d.notitieIntern}</p>}
          </div>
        </div>
      )}
      {d.overlappend.length > 0 && (
        <div className="kvd__melding" role="status">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{t("klantverzoeken.overlappend", { args: [d.overlappend.join(", ")] })}</span>
        </div>
      )}

      <div className="kvd__acties">
        {kan("CLAIMEN") && d.status === "NIEUW" && (
          <Button variant="secondary" disabled={bezig} onClick={() => void doe("claim")}>
            <UserCheck size={16} aria-hidden="true" /> {t("klantverzoeken.actie.claimen")}
          </Button>
        )}
        {kan("AANVULLING") && (
          <Button variant="secondary" disabled={bezig} onClick={() => setVenster("aanvulling")}>
            <MessageSquareReply size={16} aria-hidden="true" /> {t("klantverzoeken.actie.aanvulling")}
          </Button>
        )}
        {kan("AFWIJZEN") && (
          <Button variant="secondary" disabled={bezig} onClick={() => setVenster("afwijzen")}>
            <X size={16} aria-hidden="true" /> {t("klantverzoeken.actie.afwijzen")}
          </Button>
        )}
        {kan("OPNIEUW") && (
          <Button variant="secondary" disabled={bezig} onClick={() => void doe("opnieuw")}>
            <Repeat size={16} aria-hidden="true" /> {t("klantverzoeken.actie.opnieuw")}
          </Button>
        )}
        {kan("VOORSTEL") && (
          <Button variant="secondary" disabled={bezig} onClick={() => setVenster("voorstel")}>
            <Send size={16} aria-hidden="true" /> {t("klantverzoeken.actie.voorstel")}
          </Button>
        )}
        {kan("GOEDKEUREN") && (
          <Button variant="primary" disabled={bezig} onClick={() => setVenster("goedkeuren")}>
            <Check size={16} aria-hidden="true" /> {registratie ? t("klantverzoeken.actie.registratieGoedkeuren") : t("klantverzoeken.actie.goedkeuren")}
          </Button>
        )}
      </div>
      {fout && venster == null && <p className="fawkes-error" role="alert">{fout}</p>}

      <div className="kvd__raster">
        <div className="kvd__kolom">
          {zichtbareConflict && <Vergelijking t={t} v={zichtbareConflict} />}
          <Blok titel={t("klantverzoeken.aanvraag")} icoon={<ClipboardList size={16} />}>
            {d.reactieKlant && (
              <p className="kvd__reactie">
                <strong>{t("klantverzoeken.reactieAanKlant")}</strong> {d.reactieKlant}
              </p>
            )}
            {reserveringType && reservering.data?.data ? (
              <ReserveringBlok t={t} locale={locale} r={reservering.data.data} kanVoorstellen={kan("VOORSTEL")}
                onVoorstel={(prijs) => { setVoorstelPrijs(prijs); setFout(null); setVenster("voorstel"); }} />
            ) : d.type === "DIER_NIEUW" ? (
              <DierNieuwBlok t={t} locale={locale} verzoekId={d.id} inhoud={d.inhoud} />
            ) : (
              <InhoudTabel inhoud={d.inhoud} />
            )}
            {d.type === "DOCUMENT_UPLOAD" && <BestandenBlok t={t} verzoekId={d.id} />}
            {d.type === "DIER_WIJZIGING" && (
              <BoekjeBlok t={t} verzoekId={d.id} verwacht={(d.inhoud as Record<string, unknown> | null)?.boekje === true} />
            )}
          </Blok>
          {registratie && (
            <Blok titel={t("klantverzoeken.matches.titel")} icoon={<UserCheck size={16} />}>
              {!matches || matches.length === 0 ? (
                <p className="kvd__stil">{t("klantverzoeken.matches.geen")}</p>
              ) : (
                matches.map((m) => (
                  <div key={m.klantId} className="kvd__match">
                    <div className="kvd__matchkop">
                      <strong>{m.naam ?? `#${m.klantId}`}</strong>
                      <span className="kvd__stil">
                        {m.redenen.map((r) => t(`klantverzoeken.matches.reden.${r}`)).join(", ")}
                        {!m.actief && ` · ${t("klantverzoeken.matches.inactief")}`}
                      </span>
                      {kan("GOEDKEUREN") && (
                        <Button variant="secondary" onClick={() => { setKoppelKlant(m.klantId); setVenster("goedkeuren"); }}>
                          {t("klantverzoeken.matches.koppelen")}
                        </Button>
                      )}
                    </div>
                    <table className="kvd__tabel">
                      <thead>
                        <tr>
                          <th />
                          <th>{t("klantverzoeken.matches.registratie")}</th>
                          <th>{t("klantverzoeken.matches.klant")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.vergelijking.map((v) => (
                          <tr key={v.veld} data-verschil={v.gelijk ? undefined : "ja"}>
                            <th>{v.veld}</th>
                            <td>{v.registratie}</td>
                            <td>{v.klant}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </Blok>
          )}
          {d.voorstellen.length > 0 && (
            <Blok titel={t("klantverzoeken.voorstellen")} icoon={<Send size={16} />}>
              {d.voorstellen.map((v) => (
                <div key={v.id} className="kvd__voorstel">
                  <div className="kvd__matchkop">
                    <strong>{t("klantverzoeken.voorstelVersie", { args: [v.versie] })}</strong>
                    <StatusBadge tone={v.status === "OPEN" ? "warning" : v.status === "GEACCEPTEERD" ? "success" : "neutral"}>
                      {t(`klantverzoeken.voorstelStatus.${v.status}`)}
                    </StatusBadge>
                  </div>
                  <p>{v.reden}</p>
                  <InhoudTabel inhoud={leesJson(v.payloadJson) ?? {}} />
                  <p className="kvd__stil">
                    {t("klantverzoeken.voorstelDoor", { args: [v.voorgesteldDoor, formatDateTime(v.voorgesteldOp, locale), formatDate(v.expiresAt, locale)] })}
                  </p>
                </div>
              ))}
            </Blok>
          )}
        </div>
        <div className="kvd__kolom">
          <Blok titel={t("klantverzoeken.historie")} icoon={<History size={16} />}>
            <ol className="kvd__tijdlijn">
              {d.historie.map((h) => (
                <li key={h.id}>
                  <strong>{t(`klantverzoeken.status.${h.naar}`)}</strong>
                  <span className="kvd__stil">
                    {" "}· {formatDateTime(h.op, locale)} · {h.door === "MEDEWERKER" ? h.doorMedewerker : t(`klantverzoeken.door.${h.door}`)}
                  </span>
                  {h.klantTekst && <p>{h.klantTekst}</p>}
                  {h.interneNotitie && <p className="kvd__intern">{h.interneNotitie}</p>}
                </li>
              ))}
            </ol>
          </Blok>
          {d.versies.length > 1 && (
            <Blok titel={t("klantverzoeken.versies")} icoon={<History size={16} />}>
              {d.versies.map((v) => (
                <details key={v.versie} className="kvd__versie">
                  <summary>
                    {t("klantverzoeken.versie", { args: [v.versie] })} · {t(`klantverzoeken.aanleiding.${v.aanleiding}`)} · {formatDateTime(v.op, locale)}
                  </summary>
                  <InhoudTabel inhoud={leesJson(v.payloadJson) ?? {}} />
                </details>
              ))}
            </Blok>
          )}
        </div>
      </div>

      <AanvullingVenster t={t} open={venster === "aanvulling"} bezig={bezig} fout={fout}
        onClose={() => { setVenster(null); setFout(null); }}
        onVerstuur={(vraag, dagen) => doe("aanvulling", { vraag, termijnDagen: dagen })} />
      <AfwijzenVenster t={t} open={venster === "afwijzen"} bezig={bezig} fout={fout}
        onClose={() => { setVenster(null); setFout(null); }}
        onVerstuur={(redenVoorKlant, interneNotitie) => doe("afwijzen", { redenVoorKlant, interneNotitie })} />
      <GoedkeurenVenster t={t} open={venster === "goedkeuren"} bezig={bezig} fout={fout} detail={d}
        conflict={conflict} klantId={koppelKlant}
        onClose={() => { setVenster(null); setFout(null); setConflict(null); setKoppelKlant(null); }}
        onVerstuur={(waarden) => doe("goedkeuren", waarden)} />
      <VoorstelVenster key={`voorstel-${venster === "voorstel"}-${voorstelPrijs ?? ""}`} t={t}
        open={venster === "voorstel"} bezig={bezig} fout={fout} inhoud={d.inhoud} prijsVoorstel={voorstelPrijs}
        onClose={() => { setVenster(null); setFout(null); setVoorstelPrijs(null); }}
        onVerstuur={(waarden) => doe("voorstel", waarden)} />
    </div>
  );
}

// ---- onderdelen ----------------------------------------------------------------------------------

function Blok({ titel, icoon, children }: { titel: string; icoon: ReactNode; children: ReactNode }) {
  return (
    <section className="kvd__blok">
      <h2>
        {icoon} {titel}
      </h2>
      {children}
    </section>
  );
}

/** De inhoud als tabel; een lijst met objecten (zoals dieren) wordt een rij per item. */
function InhoudTabel({ inhoud }: { inhoud: Inhoud }) {
  return (
    <table className="kvd__tabel">
      <tbody>
        {Object.entries(inhoud).map(([sleutel, waarde]) =>
          Array.isArray(waarde) && waarde.every((w) => w && typeof w === "object") ? (
            waarde.map((item, i) => (
              <tr key={`${sleutel}-${i}`}>
                <th>{`${sleutel} ${i + 1}`}</th>
                <td>{Object.entries(item as Inhoud).map(([k, w]) => `${k}: ${waardeTekst(w)}`).join(" · ")}</td>
              </tr>
            ))
          ) : (
            <tr key={sleutel}>
              <th>{sleutel}</th>
              <td>
                {waarde && typeof waarde === "object" && !Array.isArray(waarde)
                  ? Object.entries(waarde as Inhoud).map(([k, w]) => `${k}: ${waardeTekst(w)}`).join(" · ")
                  : waardeTekst(waarde)}
              </td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** Driewegvergelijking (ontwerp 10.3): oud bij indienen, huidig, en wat de klant vraagt. */
function Vergelijking({ t, v }: { t: T; v: Driewegvergelijking }) {
  const sleutels = Array.from(new Set([...Object.keys(v.oud ?? {}), ...Object.keys(v.huidig ?? {}), ...Object.keys(v.gevraagd ?? {})]));
  return (
    <section className="kvd__blok kvd__blok--conflict">
      <h2>
        <AlertTriangle size={16} /> {t("klantverzoeken.conflict.titel")}
      </h2>
      <p className="kvd__stil">{t("klantverzoeken.conflict.uitleg")}</p>
      <table className="kvd__tabel">
        <thead>
          <tr>
            <th />
            <th>{t("klantverzoeken.conflict.oud")}</th>
            <th>{t("klantverzoeken.conflict.huidig")}</th>
            <th>{t("klantverzoeken.conflict.gevraagd")}</th>
          </tr>
        </thead>
        <tbody>
          {sleutels.map((k) => (
            <tr key={k} data-verschil={waardeTekst(v.oud?.[k]) !== waardeTekst(v.huidig?.[k]) ? "ja" : undefined}>
              <th>{k}</th>
              <td>{waardeTekst(v.oud?.[k])}</td>
              <td>{waardeTekst(v.huidig?.[k])}</td>
              <td>{waardeTekst(v.gevraagd?.[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

interface VensterProps {
  t: T;
  open: boolean;
  bezig: boolean;
  fout: string | null;
  onClose: () => void;
}

function Voet({ t, bezig, onClose, label, form }: { t: T; bezig: boolean; onClose: () => void; label: string; form: string }) {
  return (
    <>
      <Button variant="secondary" onClick={onClose}>{t("klantverzoeken.annuleren")}</Button>
      <Button variant="primary" type="submit" form={form} disabled={bezig}>{label}</Button>
    </>
  );
}

function AanvullingVenster({ t, open, bezig, fout, onClose, onVerstuur }: VensterProps & {
  onVerstuur: (vraag: string, dagen: number) => Promise<boolean>;
}) {
  const [vraag, setVraag] = useState("");
  const [dagen, setDagen] = useState("14");
  return (
    <Modal open={open} title={t("klantverzoeken.aanvullingVenster.titel")} onClose={onClose}
      footer={<Voet t={t} bezig={bezig} onClose={onClose} label={t("klantverzoeken.aanvullingVenster.versturen")} form="kv-aanvulling" />}>
      <form id="kv-aanvulling" className="kvd__form" onSubmit={(e) => { e.preventDefault(); void onVerstuur(vraag, Number(dagen) || 14); }}>
        <p className="kvd__stil">{t("klantverzoeken.aanvullingVenster.uitleg")}</p>
        <label>
          {t("klantverzoeken.aanvullingVenster.vraag")}
          <textarea required rows={4} value={vraag} onChange={(e) => setVraag(e.target.value)} />
        </label>
        <label>
          {t("klantverzoeken.aanvullingVenster.termijn")}
          <input type="number" min={1} max={60} value={dagen} onChange={(e) => setDagen(e.target.value)} />
        </label>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}

function AfwijzenVenster({ t, open, bezig, fout, onClose, onVerstuur }: VensterProps & {
  onVerstuur: (reden: string, notitie: string) => Promise<boolean>;
}) {
  const [reden, setReden] = useState("");
  const [notitie, setNotitie] = useState("");
  return (
    <Modal open={open} title={t("klantverzoeken.afwijzenVenster.titel")} onClose={onClose}
      footer={<Voet t={t} bezig={bezig} onClose={onClose} label={t("klantverzoeken.actie.afwijzen")} form="kv-afwijzen" />}>
      <form id="kv-afwijzen" className="kvd__form" onSubmit={(e) => { e.preventDefault(); void onVerstuur(reden, notitie); }}>
        <label>
          {t("klantverzoeken.afwijzenVenster.reden")}
          <textarea rows={3} value={reden} onChange={(e) => setReden(e.target.value)} />
        </label>
        <label>
          {t("klantverzoeken.interneNotitie")}
          <textarea rows={2} value={notitie} onChange={(e) => setNotitie(e.target.value)} />
        </label>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}

/**
 * Goedkeuren. Bij een registratie kiest de medewerker expliciet: nieuwe klant of koppelen aan een bestaande
 * klant; nooit automatisch (ontwerp 5). Bij een conflict toont het venster de vergelijking en vraagt het om
 * bewuste bevestiging.
 */
function GoedkeurenVenster({ t, open, bezig, fout, onClose, onVerstuur, detail, conflict, klantId }: VensterProps & {
  detail: Detail;
  conflict: Driewegvergelijking | null;
  klantId: number | null;
  onVerstuur: (waarden: object) => Promise<boolean>;
}) {
  const registratie = detail.type === "KLANT_REGISTRATIE";
  const dieren = Array.isArray(detail.inhoud.dieren) ? (detail.inhoud.dieren as Inhoud[]) : [];
  const [actie, setActie] = useState<"NIEUWE_KLANT" | "KOPPELEN">(klantId ? "KOPPELEN" : "NIEUWE_KLANT");
  const [koppel, setKoppel] = useState(klantId ? String(klantId) : "");
  const [aanmaken, setAanmaken] = useState<boolean[] | null>(null);
  const [notitie, setNotitie] = useState("");
  const [vorigeKlant, setVorigeKlant] = useState(klantId);
  if (klantId !== vorigeKlant) {
    setVorigeKlant(klantId);
    if (klantId) {
      setActie("KOPPELEN");
      setKoppel(String(klantId));
    }
  }
  const dierKeuze = aanmaken ?? dieren.map(() => actie === "NIEUWE_KLANT");

  function verstuur() {
    const waarden: Record<string, unknown> = { interneNotitie: notitie || undefined, conflictBevestigd: conflict != null };
    if (registratie) {
      waarden.opties = {
        actie,
        ...(actie === "KOPPELEN" ? { klantId: Number(koppel) } : {}),
        dieren: dierKeuze.map((ja) => ({ aanmaken: ja })),
      };
    }
    void onVerstuur(waarden);
  }

  return (
    <Modal open={open} size={conflict ? "wide" : "default"} title={registratie ? t("klantverzoeken.actie.registratieGoedkeuren") : t("klantverzoeken.actie.goedkeuren")}
      onClose={onClose}
      footer={<Voet t={t} bezig={bezig} onClose={onClose} form="kv-goedkeuren"
        label={conflict ? t("klantverzoeken.goedkeurenVenster.tochGoedkeuren") : t("klantverzoeken.actie.goedkeuren")} />}>
      <form id="kv-goedkeuren" className="kvd__form" onSubmit={(e) => { e.preventDefault(); verstuur(); }}>
        {registratie ? (
          <>
            <p className="kvd__stil">{t("klantverzoeken.goedkeurenVenster.registratieUitleg")}</p>
            <label className="kvd__keuze">
              <input type="radio" checked={actie === "NIEUWE_KLANT"} onChange={() => { setActie("NIEUWE_KLANT"); setAanmaken(null); }} />
              {t("klantverzoeken.goedkeurenVenster.nieuweKlant")}
            </label>
            <label className="kvd__keuze">
              <input type="radio" checked={actie === "KOPPELEN"} onChange={() => { setActie("KOPPELEN"); setAanmaken(null); }} />
              {t("klantverzoeken.goedkeurenVenster.koppelen")}
            </label>
            {actie === "KOPPELEN" && (
              <label>
                {t("klantverzoeken.goedkeurenVenster.klantnummer")}
                <input required inputMode="numeric" value={koppel} onChange={(e) => setKoppel(e.target.value.replace(/\D/g, ""))} />
              </label>
            )}
            {dieren.length > 0 && (
              <fieldset className="kvd__dieren">
                <legend>{t("klantverzoeken.goedkeurenVenster.dierenAanmaken")}</legend>
                {dieren.map((dier, i) => (
                  <label key={i} className="kvd__keuze">
                    <input type="checkbox" checked={dierKeuze[i]}
                      onChange={(e) => setAanmaken(dierKeuze.map((ja, j) => (j === i ? e.target.checked : ja)))} />
                    {waardeTekst(dier.naam)} <span className="kvd__stil">({waardeTekst(dier.soort)}{dier.ras ? `, ${waardeTekst(dier.ras)}` : ""})</span>
                  </label>
                ))}
              </fieldset>
            )}
          </>
        ) : (
          <p className="kvd__stil">{t("klantverzoeken.goedkeurenVenster.uitleg")}</p>
        )}
        {conflict && <Vergelijking t={t} v={conflict} />}
        <label>
          {t("klantverzoeken.interneNotitie")}
          <textarea rows={2} value={notitie} onChange={(e) => setNotitie(e.target.value)} />
        </label>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}

/** Tegenvoorstel (ontwerp 10.5): de aanvraag als startpunt; de klant ziet straks beide naast elkaar. */
function VoorstelVenster({ t, open, bezig, fout, onClose, onVerstuur, inhoud, prijsVoorstel }: VensterProps & {
  inhoud: Inhoud;
  /** Voorgevulde prijs, bijvoorbeeld de nieuwe prijs van een reserveringsaanvraag */
  prijsVoorstel?: number | null;
  onVerstuur: (waarden: object) => Promise<boolean>;
}) {
  const tekstvelden = Object.entries(inhoud).filter(([, w]) => w == null || typeof w !== "object");
  const [velden, setVelden] = useState<Record<string, string>>(() =>
    Object.fromEntries(tekstvelden.map(([k, w]) => [k, waardeTekst(w)])));
  const [reden, setReden] = useState("");
  const [prijs, setPrijs] = useState(prijsVoorstel != null ? prijsVoorstel.toFixed(2).replace(".", ",") : "");
  const [bijAfwijzen, setBijAfwijzen] = useState<"TERUG_NAAR_BEOORDELING" | "AFWIJZEN">("TERUG_NAAR_BEOORDELING");
  const [afwijsReden, setAfwijsReden] = useState("");

  function verstuur() {
    // Wat geen tekst is (lijsten, objecten) blijft zoals de klant het vroeg.
    const nieuw: Inhoud = { ...inhoud };
    Object.entries(velden).forEach(([k, w]) => {
      nieuw[k] = typeof inhoud[k] === "number" && w !== "" && !Number.isNaN(Number(w)) ? Number(w) : w;
    });
    void onVerstuur({
      inhoud: nieuw,
      prijs: prijs ? { totaal: Number(prijs.replace(",", ".")) } : null,
      reden,
      bijAfwijzen,
      afwijsReden: bijAfwijzen === "AFWIJZEN" ? afwijsReden : null,
    });
  }

  return (
    <Modal open={open} size="wide" title={t("klantverzoeken.voorstelVenster.titel")} onClose={onClose}
      footer={<Voet t={t} bezig={bezig} onClose={onClose} label={t("klantverzoeken.voorstelVenster.versturen")} form="kv-voorstel" />}>
      <form id="kv-voorstel" className="kvd__form" onSubmit={(e) => { e.preventDefault(); verstuur(); }}>
        <p className="kvd__stil">{t("klantverzoeken.voorstelVenster.uitleg")}</p>
        <table className="kvd__tabel">
          <thead>
            <tr>
              <th />
              <th>{t("klantverzoeken.voorstelVenster.gevraagd")}</th>
              <th>{t("klantverzoeken.voorstelVenster.voorstel")}</th>
            </tr>
          </thead>
          <tbody>
            {tekstvelden.map(([k, w]) => (
              <tr key={k} data-verschil={velden[k] !== waardeTekst(w) ? "ja" : undefined}>
                <th>{k}</th>
                <td>{waardeTekst(w)}</td>
                <td>
                  <input aria-label={k} value={velden[k] ?? ""} onChange={(e) => setVelden({ ...velden, [k]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <label>
          {t("klantverzoeken.voorstelVenster.reden")}
          <textarea required rows={3} value={reden} onChange={(e) => setReden(e.target.value)} />
        </label>
        <label>
          {t("klantverzoeken.voorstelVenster.prijs")}
          <input inputMode="decimal" value={prijs} onChange={(e) => setPrijs(e.target.value)} />
        </label>
        <label>
          {t("klantverzoeken.voorstelVenster.bijAfwijzen")}
          <select value={bijAfwijzen} onChange={(e) => setBijAfwijzen(e.target.value as typeof bijAfwijzen)}>
            <option value="TERUG_NAAR_BEOORDELING">{t("klantverzoeken.voorstelVenster.terug")}</option>
            <option value="AFWIJZEN">{t("klantverzoeken.voorstelVenster.afwijzen")}</option>
          </select>
        </label>
        {bijAfwijzen === "AFWIJZEN" && (
          <label>
            {t("klantverzoeken.voorstelVenster.afwijsReden")}
            <textarea rows={2} value={afwijsReden} onChange={(e) => setAfwijsReden(e.target.value)} />
          </label>
        )}
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}
